import { isNotNullish } from '@nx/stdlib';
import type { Item } from '@query/services/api';
import { ROOT_ITEM_ID } from '@query/utils/saved-cypher-utils';

export const NEW_FOLDER_PREFIX = 'New Folder';

export type SavedCypherLoadingState = {
  isLoading: boolean;
  isError: boolean;
  isSuccess: boolean;
};

type SavedCypherItemBase = {
  id: string;
  name: string;
  order: number;
  isNew?: boolean;
};

export type SavedCypherQuery = SavedCypherItemBase & {
  query: string;
};

export type SavedCypherFolder = SavedCypherItemBase & {
  children: TreeItems;
};

export type SavedCypherItem = SavedCypherQuery | SavedCypherFolder;

type FlatSavedCypherItemBase = {
  parentId: string | null;
  depth: number;
};

export type FlatSavedCypherFolder = FlatSavedCypherItemBase & SavedCypherFolder;
export type FlatSavedCypherQuery = FlatSavedCypherItemBase & SavedCypherQuery;
export type FlatSavedCypherItem = FlatSavedCypherQuery | FlatSavedCypherFolder;

export const isSavedCypherQuery = (
  item: Item | SavedCypherItem | FlatSavedCypherItem,
): item is SavedCypherQuery | FlatSavedCypherQuery => 'query' in item && isNotNullish(item.query);

export const isSavedCypherFolder = (
  item: Item | SavedCypherItem | FlatSavedCypherItem,
): item is SavedCypherFolder | FlatSavedCypherFolder => !isSavedCypherQuery(item) && 'children' in item;

export type TreeItems = SavedCypherItem[];

export type FlatTreeItems = FlatSavedCypherItem[];

export type FlatSavedCypherTree = {
  savedCypherTreeId: string;
  flatSavedCypherTree: FlatTreeItems;
  flatSavedCypherTreeState: SavedCypherLoadingState;
};

type UpdateSavedCypherPayload = {
  id: string;
  isOverwrite?: boolean;
};

export type UpdateSavedCypherQueryPayload = {
  newValues: Partial<FlatSavedCypherQuery>;
} & UpdateSavedCypherPayload;

export type UpdateSavedCypherFolderPayload = {
  name: string;
} & UpdateSavedCypherPayload;

export const isUpdateSavedCypherQueryPayload = (
  payload: UpdateSavedCypherFolderPayload | UpdateSavedCypherQueryPayload,
): payload is UpdateSavedCypherQueryPayload => {
  return 'newValues' in payload;
};

export type CreateSavedCypherQueryPayload = {
  name: string;
  query: string;
  parentId: string | null;
};

export type CreateSavedCypherFolderPayload = {
  name: string | null;
  parentId: string | null;
};

export type DeleteSavedCyphersPayload = {
  ids: string[];
};

export const isCreateSavedCypherQueryPayload = (
  payload: CreateSavedCypherQueryPayload | CreateSavedCypherFolderPayload,
): payload is CreateSavedCypherQueryPayload => {
  return 'query' in payload;
};

export type ImportedBrowserFolder = {
  id: string;
  name: string;
  isStatic?: boolean;
};

export type ImportedBrowserDocument = {
  id: string;
  content: string;
  folder?: string;
  isStatic?: boolean;
};

export function isChild(item: FlatSavedCypherItem): item is FlatSavedCypherItem & { parentId: string } {
  return item.parentId !== null && item.parentId !== ROOT_ITEM_ID && item.parentId !== '';
}
